<template>
  <a-modal
    :open="Boolean(session)"
    :closable="false"
    :mask-closable="false"
    :keyboard="false"
    :footer="null"
    width="440px"
    :destroy-on-close="true"
  >
    <section v-if="session" class="ad-player" :class="`ad-player--${session.campaign.format}`">
      <div class="ad-player__stage">
        <img
          v-if="session.campaign.coverUrl"
          :src="session.campaign.coverUrl"
          :alt="session.campaign.title"
        />
        <i v-else :class="formatIcon" aria-hidden="true" />
        <span class="ad-player__label">广告播放中</span>
        <strong>{{ remaining }}<small> 秒</small></strong>
      </div>
      <h2>{{ session.campaign.title }}</h2>
      <p>{{ session.campaign.description }}</p>
      <a-progress :percent="progress" :show-info="false" stroke-color="var(--aw-primary)" />
      <a-button
        type="primary"
        block
        :disabled="remaining > 0"
        :loading="store.completing"
        @click="complete"
      >
        {{
          remaining > 0 ? '请完整观看后领取奖励' : `领取 ${session.campaign.rewardCredits} Credits`
        }}
      </a-button>
      <span class="ad-player__notice">完成后将自动记入你的账户积分</span>
    </section>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { useAdvertisingStore } from '@/stores/advertising.store';
const store = useAdvertisingStore();
const session = computed(() => store.activeSession);
const remaining = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;
const progress = computed(() =>
  session.value
    ? Math.min(
        100,
        Math.max(0, (1 - remaining.value / session.value.campaign.durationSeconds) * 100),
      )
    : 0,
);
const formatIcon = computed(
  () =>
    ({ video: 'ri-play-circle-line', image: 'ri-image-2-line', interactive: 'ri-gamepad-line' })[
      session.value?.campaign.format ?? 'video'
    ],
);
function syncTimer(): void {
  if (timer) clearInterval(timer);
  if (!session.value) return;
  const started = new Date(session.value.startedAt).getTime();
  const tick = () => {
    remaining.value = Math.max(
      0,
      Math.ceil(session.value!.campaign.durationSeconds - (Date.now() - started) / 1000),
    );
  };
  tick();
  timer = setInterval(tick, 250);
}
async function complete(): Promise<void> {
  try {
    await store.complete();
    store.closeSession();
  } catch (error) {
    message.error((error as { message?: string }).message ?? '奖励领取失败，请稍后再试');
  }
}
watch(session, syncTimer, { immediate: true });
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<style scoped>
.ad-player {
  display: grid;
  gap: 14px;
  padding: 4px;
  text-align: center;
}
.ad-player__stage {
  position: relative;
  display: grid;
  min-height: 184px;
  place-items: center;
  overflow: hidden;
  border-radius: 14px;
  color: #fff;
  background: linear-gradient(135deg, #172554, #312e81 56%, #0f766e);
}
.ad-player__stage img {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.46;
}
.ad-player__stage > i {
  font-size: 56px;
  opacity: 0.9;
}
.ad-player__stage strong {
  position: relative;
  font-size: 42px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.ad-player__stage strong small {
  font-size: 15px;
  font-weight: 600;
}
.ad-player__label {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgb(15 23 42 / 65%);
  font-size: 12px;
}
.ad-player h2 {
  margin: 4px 0 0;
  color: var(--aw-text);
  font-size: 19px;
}
.ad-player p {
  margin: -7px 0 0;
  color: var(--aw-text-secondary);
}
.ad-player__notice {
  color: var(--aw-text-tertiary);
  font-size: 12px;
}
</style>
